import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { datadogLogs } from "@datadog/browser-logs";
import type { Nullable } from "src/features/kyc/imports/types";
import { requestIdentificationForSuspendedAccount } from "src/features/kyc/api/identityValidator.api";
import { ToastType } from "src/features/kyc/imports/enums";
import { useToast } from "src/features/kyc/imports/hooks";
import { sharedMessages } from "src/features/kyc/imports/intl";
import { isAbortError } from "src/features/kyc/imports/utils";

export const useKycLink = () => {
  const { notify } = useToast();
  const { formatMessage } = useIntl();

  const [isKycLinkFetching, setIsKycLinkFetching] = useState(false);

  const abortControllerRef = useRef<Nullable<AbortController>>(null);

  const fetchKycLinkAndOpen = useCallback(async () => {
    setIsKycLinkFetching(true);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const { externalLink } = await requestIdentificationForSuspendedAccount(
        abortControllerRef.current.signal
      );

      window.open(externalLink, "_blank");
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      notify({
        type: ToastType.REGULAR,
        title: formatMessage(sharedMessages.genericError),
      });

      datadogLogs.logger.warn(
        "Failed to request identification for suspended account",
        error as Error
      );
    } finally {
      setIsKycLinkFetching(false);
      abortControllerRef.current = null;
    }
  }, [formatMessage, notify]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  return { isKycLinkFetching, fetchKycLinkAndOpen };
};
